import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const xFollowersExporterProProduct: Product = {
  id: "x-followers-exporter-pro",
  slug: "x-followers-exporter-pro",
  name: "Followers Exporter Pro for X (Twitter) — Export to CSV/JSON",
  alternateName: "Followers Exporter Pro for X (Twitter)",
  shortName: "Followers Exporter for X",
  url: "https://plugmonkey.xyz/product/x-followers-exporter-pro/",
  chromeStoreRedirect: "/redirect/x-followers-exporter-pro-chrome-store/",
  description:
    "Export any X/Twitter user's followers or following to CSV/JSON with 20+ metadata fields (bio, location, engagement). Free Chrome extension.",
  shortDescription:
    "Export any X (Twitter) user's followers or following list to CSV with 20+ metadata fields for deep analysis.",
  image: ["https://plugmonkey.xyz/product/x-followers-exporter-pro/og-image.png"],
  icon: {
    name: "HiDatabase",
    backgroundClasses: "bg-black",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Social Media Analytics",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Social Media Analysts, Marketers, Researchers",
  freeOffer: {
    name: "Free Version",
    description:
      "3 exports per week with 250 records maximum per export, all 20+ metadata fields included",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/x-followers-exporter-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/14cbf357-47ab-4d92-816a-fa3a50f5f333",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/728f2417-fb42-4bf7-a05c-3023218ee6ee",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/e442f967-bc96-4501-ad2e-11ddf15a7adc",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.9",
    reviewCount: "89",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "5,470",
  keywords:
    "X followers export, Twitter data export, CSV export, follower analysis, audience research, social media analytics",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.6.24",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.6.24",
  hero: {
    headline:
      "<span class='text-primary'>Followers Exporter Pro for X</span> — Export 𝕏 (Twitter) Followers to CSV With 20+ Metadata Fields",
    subheadline:
      "Export any X (Twitter) user's followers or following list to CSV. Perfect for audience research, competitor analysis, and data-driven insights.",
    ctaText: "Install Free",
    badgeText: "v1.6.24 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "Complete CSV Exports",
      description:
        "Export any X user's followers or following list with 20+ metadata fields including user ID, bio, follower counts, and engagement metrics.",
      icon: "HiDatabase",
    },
    {
      title: "Privacy First",
      description:
        "Chrome extension that works directly in your browser. No API keys, no OAuth, no data shared with third parties.",
      icon: "HiShieldCheck",
    },
    {
      title: "Fast & Reliable",
      description:
        "Export thousands of profiles in minutes. Works on any public X account - competitors, influencers, or your own following.",
      icon: "HiLightningBolt",
    },
  ],
  longFormFeatures: [
    {
      title: "20+ Metadata Fields",
      description:
        "Every export includes comprehensive data: User ID, Bio, Follower/Following counts, Verified status, Location, Website, Account age, and more.",
    },
    {
      title: "Universal Data Format",
      description:
        "Data is exported in standard CSV format, instantly ready for Excel, Google Sheets, Python, R, or any database import.",
    },
    {
      title: "Paginated Table View",
      description:
        "See your data before downloading. 20 records per page, beautifully formatted. Review and verify before exporting.",
    },
    {
      title: "Auto-Fill Username",
      description:
        "On any profile page, the username is automatically detected and filled in. One less step between you and your data.",
    },
  ],
  faq: [
    {
      question: "Can I export followers from any X (Twitter) account?",
      answer:
        "Yes! Followers Exporter Pro for X works on any public X account, not just your own. You can export followers or following lists from competitors, influencers, or any account you want to analyze. No authorization required from the account owner.",
    },
    {
      question: "What data fields are included in the export?",
      answer:
        "Every export includes 20+ metadata fields: User ID, Avatar URL, Name, Username, Bio, Tweets Count, Followers Count, Following Count, Favorites Count, Media Count, Professional Account Info, Location, Website URL, Verified Status (legacy), Blue Verified Status, Verified Type, Protected Status, Can DM, Created At (account age), and Profile Banner URL. All fields are included in both free and Pro versions.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version allows 3 exports per week (resets every Monday) with a maximum of 250 records per export. The Pro version offers unlimited exports, unlimited records per export (export 100K+ followers), and priority support.",
    },
    {
      question: "Do I need API keys or OAuth permissions?",
      answer:
        "No! That's the beauty of Followers Exporter Pro for X. It's a Chrome extension that works directly in your browser using your existing X session. No API keys, no OAuth dance, no complex setup. Just install and start exporting.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "What format is the exported data?",
      answer:
        "Data is exported in CSV (Comma-Separated Values) format, which opens in Excel, Google Sheets, or any data tool. It's ready for pivot tables, Python/R analysis, database imports, or any other use case you have.",
    },
    {
      question: "How long does it take to export a large list?",
      answer:
        "Followers Exporter Pro for X fetches 200 records per request with smart pagination. For reference, a 10K follower list takes about 2-3 minutes to export. A 100K list takes about 20-25 minutes. The extension shows real-time progress so you know exactly how much is left.",
    },
    {
      question: "Is my data safe and private?",
      answer:
        "Yes. All processing happens locally in your browser. We never see or store your exported data. The extension doesn't send data to any third-party servers. Your exports stay on your computer.",
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer:
        "Yes, we offer a 30-day money-back guarantee. If Followers Exporter Pro for X isn't working as described, contact our support team within 30 days of purchase — we'll troubleshoot first (most issues resolve in a single reply), and if we can't resolve it, you get a full refund.",
    },
    {
      question: "Does this work on X's new interface and rate limits?",
      answer:
        "Yes! Followers Exporter Pro for X is actively maintained and works with X's current interface. The extension automatically respects Twitter's API limits with smart delays between requests to ensure reliable exports without hitting rate limits.",
    },
    {
      question: "How does Followers Exporter Pro for X compare to Phantombuster?",
      answer:
        'Phantombuster is a general-purpose automation platform that handles many social networks, while Followers Exporter Pro for X is purpose-built specifically for exporting X (Twitter) follower data with 20+ metadata fields and zero setup. If you want a focused, privacy-first tool that just works, Followers Exporter Pro for X is the better choice. Read the <a href="/comparison/x-followers-exporter-pro-vs-phantombuster/" style="color: var(--primary); text-decoration: underline;">full comparison with Phantombuster</a> or see how it ranks among the <a href="/best/best-twitter-follower-export-tools/" style="color: var(--primary); text-decoration: underline;">best Twitter follower export tools</a>.',
    },
  ],
  useCases: [
    {
      title: "Marketers",
      description:
        "Export competitor followers to identify target audience demographics and interests. Build lookalike audience lists for paid campaigns.",
    },
    {
      title: "Influencers",
      description:
        "Export followers of bigger accounts in your niche. Find potential collaboration partners and engagement targets based on follower overlap.",
    },
    {
      title: "Researchers",
      description:
        "Study social network patterns, community structures, and follower dynamics. Export data for academic analysis and visualization.",
    },
    {
      title: "Data Analysts",
      description:
        "Get raw CSV data ready for Python/R analysis. Merge with other datasets, visualize trends, build insights, and create reports.",
    },
    {
      title: "Agencies",
      description:
        "Audit client accounts, analyze follower quality, identify fake accounts, measure campaign impact, and provide data-driven recommendations.",
    },
    {
      title: "Business Development",
      description:
        "Build targeted outreach lists based on bio keywords, follower counts, and verified status. Identify potential customers and partners.",
    },
  ],
  productHunt: {
    slug: "x-followers-exporter-pro-twitter",
    id: "x-followers-exporter-pro",
    title: "Followers Exporter Pro for X",
    rating: "4.9",
    reviewCount: "89",
  },
  demoSection: {
    title: "See Followers Exporter Pro for X in Action",
    description: "Watch how quickly you can export follower data.",
    slides: getProductScreenshots("x-followers-exporter-pro"),
  },
  technicalSpecs: {
    title: "20+ Data Points Per User",
    columns: 2,
    fields: [
      { icon: "HiHashtag", label: "User ID", value: "Unique Identifier" },
      { icon: "HiUser", label: "Example Name", value: "Display Name" },
      { icon: "HiAtSymbol", label: "Username", value: "@handle" },
      { icon: "HiMenuAlt2", label: "Bio", value: "Profile Description" },
      { icon: "HiUsers", label: "Followers Count", value: "Audience Size" },
      { icon: "HiUserGroup", label: "Following Count", value: "Friends Count" },
      { icon: "HiPencil", label: "Tweets Count", value: "Post Volume" },
      { icon: "HiCalendar", label: "Account Age", value: "Created At Date" },
      { icon: "HiLocationMarker", label: "Location", value: "Geographic Data" },
      { icon: "HiLink", label: "Website URL", value: "Bio Link" },
      { icon: "HiCheckCircle", label: "Verified Status", value: "Blue Check" },
      { icon: "HiShieldCheck", label: "Protected", value: "Private/Public" },
      { icon: "HiChat", label: "DM Status", value: "Can DM?" },
      { icon: "HiPhotograph", label: "Avatar URL", value: "Profile Image" },
      { icon: "HiPhotograph", label: "Banner URL", value: "Header Image" },
      { icon: "HiHeart", label: "Favorites", value: "Likes Count" },
      { icon: "HiCollection", label: "Media Count", value: "Photos/Videos" },
      { icon: "HiBriefcase", label: "Professional", value: "Biz Status" },
      { icon: "HiTranslate", label: "Translator", value: "Type" },
      { icon: "HiCode", label: "Raw JSON", value: "Full Object" },
    ],
  },
  ctaSection: {
    title: "Ready to Export Your First 𝕏 Follower List?",
    subtitle:
      "Join marketers, researchers, and analysts who trust Followers Exporter Pro for X for their data needs",
  },
  relatedResources: [
    {
      category: "best",
      slug: "best-twitter-follower-export-tools",
      title: "Best X Follower Export Tools 2026 — Ranked",
      description: "Independent ranking of follower-export tools. Followers Exporter Pro for X #1.",
    },
    {
      category: "guide",
      slug: "how-to-export-twitter-followers",
      title: "How to Export X / Twitter Followers to CSV",
      description: "Step-by-step workflow for accounts with 1K to 200K+ followers.",
    },
    {
      category: "faq",
      slug: "can-i-export-competitor-followers-on-x",
      title: "Can I Export Competitor Followers on X?",
      description: "Yes — and here's how to use that data for ICP research + outreach.",
    },
    {
      category: "faq",
      slug: "can-i-see-who-unfollowed-me-on-twitter",
      title: "Can I See Who Unfollowed Me on X?",
      description: "Snapshot-comparison workflow using exported follower lists.",
    },
    {
      category: "comparison",
      slug: "x-followers-exporter-pro-vs-phantombuster",
      title: "Followers Exporter Pro for X vs PhantomBuster",
      description: "Browser-based simplicity vs cloud-based complexity — full breakdown.",
    },
    {
      category: "alternative",
      slug: "followerwonk",
      title: "Followerwonk Alternative — Browser-Based Replacement",
      description:
        "Followerwonk shut down. Followers Exporter Pro for X is the closest functional replacement.",
    },
  ],
  testimonials: [
    {
      text: "Exported 14K followers of a competitor account and had a segmented CSV in under 5 minutes. The 20+ columns are overkill in the best way — follower count, bio, join date, verified status, all there.",
      author: "Riku A.",
      role: "Audience research at a B2B SaaS startup",
      rating: 5,
    },
    {
      text: "We run influencer campaigns and needed follower overlap data across 30+ accounts. Pagination is a little slow on 100K+ lists, but the data quality is better than any API tool I've used. Exports open clean in Google Sheets.",
      author: "Ben S.",
      role: "Runs paid partnerships at a DTC brand",
      rating: 4,
    },
    {
      text: "Using this for academic research on political Twitter. Exported 200K+ followers across 12 accounts for network analysis. The raw JSON mode saved me from writing my own scraper.",
      author: "Amara D.",
      role: "PhD candidate, computational social science",
      rating: 5,
    },
    {
      text: "I export the followers of a few well-known engineers to find people in our niche. The join date and bio columns let me filter for active accounts. Twelve thousand rows opened clean in Sheets.",
      author: "Noor A.",
      role: "Technical recruiter",
      rating: 5,
    },
  ],
  howTo: {
    name: "How to Export X (Twitter) Followers to CSV",
    description:
      "Learn how to export any X user's followers or following list to CSV with 20+ metadata fields in just 4 simple steps",
    totalTime: "PT2M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Click the Export Button",
        text: "Click the button that appears on any X.com page (top-right corner).",
        position: 1,
      },
      {
        name: "Enter Username",
        text: "Enter any X username (or use the auto-filled one if you're on a profile page).",
        position: 2,
      },
      {
        name: "Choose Export Type",
        text: "Select either 'Followers' or 'Following' depending on what data you want to export.",
        position: 3,
      },
      {
        name: "Export to CSV",
        text: "Click 'Start Export' and watch the data load in a beautiful table, then export to CSV with one click.",
        position: 4,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "FaXTwitter",
    backgroundIconOpacity: "opacity-10",
    backgroundIconTransform: "transform -rotate-6",
    features: [
      { label: "Format", value: "CSV / JSON" },
      { label: "Fields", value: "25+ Metadata" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 2,
  headerOrder: 400,
  pricing: {
    tagline: "Export Follower and Following Data for Deep Analysis",
    freeFeatures: [
      "3 exports per week",
      "250 records per export",
      "10+ essential data fields",
      "CSV format (Excel/Sheets compatible)",
    ],
    proFeatures: [
      "Unlimited exports",
      "Unlimited records per export",
      "20+ comprehensive data fields",
      "Export any user's followers/following",
      "Advanced filtering options",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Ideal for audience research and competitive analysis",
  },
  flowData: {
    installation: {
      headline: "Your data export engine is ready.",
      subheadline: "Start building your database in under a minute. Here is how.",
      guideSteps: [
        {
          title: "Pin for Quick Access",
          description:
            "Click the puzzle piece in Chrome and pin 𝕏 Followers Exporter Pro so it's always there when you find a profile to analyze.",
          image: "pin-extension",
        },
        {
          title: "Open the Exporter on X.com",
          description:
            "Visit any page on x.com and click the 'Open Followers Exporter Pro for X' button that appears on the page.",
          image: "navigate-x",
          screenshot: "screenshot-1.png",
          ctaText: "Open X.com",
          ctaUrl: "https://x.com",
        },
        {
          title: "Enter a Username & Export",
          description:
            "Type any X handle, choose Followers or Following, click Start Export, and download your CSV.",
          image: "export-data",
          screenshot: "screenshot-1.png",
        },
      ],
      primaryCtaText: "Open X.com and Export Data",
      primaryCtaUrl: "https://x.com",
      emailCapture: {
        headline: "Master your audience data",
        description:
          "Get our free guide on '5 Ways to Use Follower Data for Growth' plus weekly data insights.",
        buttonText: "Send me the guide",
      },
    },
    uninstallation: {
      headline: "We missed the mark?",
      subheadline: "Help us build a better data tool. Why are you uninstalling?",
      survey: {
        question: "Why are you uninstalling Followers Exporter Pro for X?",
        options: [
          "The export failed or was too slow",
          "Data format wasn't what I needed",
          "Too expensive for my needs",
          "Found a better tool",
          "Finished my project",
        ],
      },
    },
  },
};
